/**
 * 🌍 GLOBAL STANDART KOD (TÜM PROJELER İÇİN GEÇERLİDİR)
 * Tek kaynak qubepro-core; resmî sync ile dağıtılır.
 */

import type { AppInfo } from '../context/AppsContext';
import { getRegistryCodeForSourceApp } from '../components/shared/AppLogo';

export type AuthorizedPortalAction = {
  appCode: string;
  path: string;
  permissionCode?: string;
  openInPanel?: boolean;
};

export type NotificationSource = { sourceApp?: string | null; link?: string | null };

export function getNotificationPlatformOrigin(): string {
  const configured = String(import.meta.env.VITE_PLATFORM_URL || import.meta.env.VITE_BASE_URL || '').trim();
  try { return new URL(configured || window.location.origin).origin; }
  catch { return ''; }
}

const SAFE_PATH = /^\/[A-Za-z0-9/_-]+$/;
const READ_PERMISSION = /^[A-Za-z][A-Za-z0-9_-]*:read$/;

function safePath(path: string): boolean {
  return SAFE_PATH.test(path)
    && !path.includes('//')
    && !path.split('/').some((part) => part === '.' || part === '..');
}

/** Core filters the catalog by the authenticated actor. A failed refresh yields no actions. */
export async function loadAuthorizedNotificationRoutes(): Promise<AuthorizedPortalAction[]> {
  const { default: axios } = await import('../api/axios');
  const response = await axios.get('/api/users/me/portal-actions');
  const data: unknown = response.data?.data ?? response.data;
  if (!Array.isArray(data)) return [];
  return data.filter((value): value is AuthorizedPortalAction => (
    value !== null && typeof value === 'object'
    && typeof value.appCode === 'string'
    && typeof value.path === 'string'
    && typeof value.permissionCode === 'string'
  ));
}

/**
 * Link is untrusted notification data. Only an exact, active, permission-backed
 * menu route from Core and a registered source application may be opened.
 * The destination still enforces its own session, company and record access.
 */
export function resolveNotificationSourceLink(
  item: NotificationSource,
  actions: AuthorizedPortalAction[],
  apps: AppInfo[],
  permissions: readonly string[],
  isAdmin: boolean,
  platformOrigin: string,
): string | null {
  const sourceCode = getRegistryCodeForSourceApp(item.sourceApp);
  if (!sourceCode || !item.link || typeof item.link !== 'string') return null;
  const app = apps.find((candidate) => candidate.code.toUpperCase() === sourceCode);
  if (!app || !safePath(app.path)) return null;
  const raw = item.link.trim();
  if (!raw || raw.length > 2048 || !raw.startsWith('/') || raw.startsWith('//')
    || /[\\\u0000-\u001f\u007f]/.test(raw) || /%(?:2f|5c|2e|00)/i.test(raw)) return null;
  let base: URL;
  let target: URL;
  try {
    base = new URL(platformOrigin);
    target = new URL(raw, base);
  } catch { return null; }
  if (!['https:', 'http:'].includes(base.protocol) || base.username || base.password
    || base.pathname !== '/' || base.search || base.hash || target.origin !== base.origin
    || target.username || target.password || target.hash || !safePath(target.pathname)) return null;
  const appPath = app.path.replace(/\/+$/, '');
  const relativePath = target.pathname.startsWith(`${appPath}/`)
    ? target.pathname.slice(appPath.length)
    : target.pathname;
  if (!safePath(relativePath)) return null;
  const allowed = actions.some((action) => {
    const permission = action.permissionCode;
    return action.appCode.toUpperCase() === sourceCode
      && !action.openInPanel
      && action.path === relativePath
      && typeof permission === 'string'
      && READ_PERMISSION.test(permission)
      && (isAdmin || permissions.includes('*') || permissions.includes(permission));
  });
  if (!allowed) return null;
  target.pathname = `${appPath}${relativePath}`;
  return target.toString();
}
